import React, { useState } from "react"
import { Modal, Typography } from "antd"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import CreateProjectForm from "./CreateProjectForm.component"

import { notify } from "shared/ui/Notifications/notify"

/**
 * AddProjectModal component - Displays a modal for creating a new project.
 *
 * This component renders a button that, when clicked, shows a modal containing
 * a form for creating a new project. The modal can be closed upon successfully
 * creating a project or by other interactions defined within the modal.
 * The project list refreshes via the create mutation's cache invalidation.
 *
 * @component
 * @returns  The AddProjectModal component.
 */
const AddProjectModal: React.FC = () => {
  const { Title } = Typography
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)

  /**
   * Handles actions after successfully creating a project.
   * It closes the modal and optionally triggers additional actions such as refreshing the project list.
   *
   * @function createProjectCallback
   * @param {string} projectId - The ID of the newly created project.
   */
  const createProjectCallback = (projectId: string): void => {
    if (projectId) {
      setIsModalVisible(false)
      notify.success(
        "Project Created Successfully!",
        "The project has been created successfully."
      )
    }
  }

  /**
   * Shows the modal by setting the modal visibility state to true.
   */
  const showModal = (): void => {
    setIsModalVisible(true)
  }

  return (
    <>
      <DataQButton onClick={showModal} type="secondary">
        Create Project
      </DataQButton>
      <Modal
        open={isModalVisible}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
      >
        <Title style={{ marginTop: "0.5em" }} level={4}>
          Create Project
        </Title>
        <CreateProjectForm
          setIsModalVisible={setIsModalVisible}
          createProjectCallback={createProjectCallback}
        />
      </Modal>
    </>
  )
}

export default AddProjectModal
